import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button } from '@lightspeed/flame/Button';
import Bone from '@lightspeed/cirrus-bone';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import find from 'lodash/find';
import filter from 'lodash/filter';
import concat from 'lodash/concat';

import history from 'pages/router/history';
import { entities } from 'pages/store/schema';
import { getData, clearData, postData, putData, deleteData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import {
  getProcessing,
  getErrors,
  getLightspeedPaymentGateways,
  getIntegratedPaymentGateways,
  getGatewayDetails,
  getMappablePaymentTypes,
  getLightspeedPaymentCardMappings,
  getValues,
} from 'pages/store/selectors';
import Layout, { LayoutContent } from 'cirrus/Layout';
import ViewError from 'pages/components/ViewError';

import LightspeedPayments from './LightspeedPayments';
import GatewayTerminalListing from './GatewayTerminalListing';
import { paths } from './routes';
import AddTerminal from './terminal/Add';
import EditTerminal from './terminal/Edit';

const merchantos = window.merchantos;

const STRINGS = {
  BUTTON: {
    ADD_PAYMENT_GATEWAY: 'Add Payment Gateway',
  },
};

class PaymentProcessing extends Component {
  constructor(props) {
    super(props);
    merchantos.asyncSubscribe.notifySubscribers('react.mount.topbar', {
      props: {
        crumbs: [
          {
            type: 'ui_tab',
            text: 'Settings',
            name: 'admin',
            icon: 'settings',
          },
          {
            text: 'Payment Processing',
          },
        ],
      },
    });

    this.state = {
      openAddTerminal: false,
      openEditTerminal: false,
    };

    this.renderGatewayTerminalListings = this.renderGatewayTerminalListings.bind(this);
    this.handleConfigureClicked = this.handleConfigureClicked.bind(this);
    this.handleAddGatewayClicked = this.handleAddGatewayClicked.bind(this);
    this.handleAddTerminalClicked = this.handleAddTerminalClicked.bind(this);
    this.createTerminal = this.createTerminal.bind(this);
    this.editTerminal = this.editTerminal.bind(this);
  }

  componentDidMount() {
    this.props.getData(
      `/API/Account/${merchantos.account.id}/GatewayConfig.json`,
      entities.gatewayConfig
    );
    this.props.getData(`/API/Account/${merchantos.account.id}/Gateway.json`, entities.gateway);
    this.props.getData(
      `/API/Account/${merchantos.account.id}/CCTerminal.json`,
      entities.ccTerminal
    );
    this.props.getData(`/API/Account/${merchantos.account.id}/Terminal.json`, entities.terminal);
  }

  componentWillUnmount() {
    this.props.clearData();
  }

  handleConfigureClicked() {
    history.push(paths.lightspeedPaymentsConfiguration);
  }

  handleAddGatewayClicked() {
    history.push(paths.addGateway);
  }

  handleAddTerminalClicked() {
    this.setState({
      openAddTerminal: true,
    });
  }

  createTerminal({ name, type, ...otherValues } = {}) {
    const { addTerminal } = this.props;
    const { gatewayConfig = {} } = this.state;

    const configuration = Object.keys(otherValues).reduce((config, key) => {
      if (otherValues[key].length) {
        config[key] = otherValues[key]; // eslint-disable-line no-param-reassign
      }

      return config;
    }, {});

    const requestData = {
      name,
      type,
      gatewayConfigID: gatewayConfig.gatewayConfigID,
      configuration,
      shopID: 0,
    };

    addTerminal(requestData, () => {
      this.setState({
        openAddTerminal: false,
      });
    });
  }

  editTerminal({ name, type, ...otherValues } = {}) {
    const { updateTerminal } = this.props;
    const { ccTerminal = {} } = this.state;

    const requestData = {
      ccTerminalID: ccTerminal.ccTerminalID,
      name,
      type,
      gatewayConfigID: ccTerminal.gatewayConfigID,
      configuration: otherValues,
    };

    updateTerminal(ccTerminal.ccTerminalID, requestData, () => {
      this.setState({
        openEditTerminal: false,
      });
    });
  }

  renderGatewayTerminalListings() {
    const {
      gatewayConfig,
      gatewayDetails,
      isLoading,
      configuredTerminals,
      deleteTerminal,
    } = this.props;
    let listings = [];

    listings = isLoading ? (
      <LayoutContent>
        <div className="cr-flex cr-justify-between cr-align-center cr-mb-2">
          <Bone width="40%" height="1rem" />
          <Bone width="15%" height="2rem" />
        </div>
        <Card>
          <CardSection>
            <div className="cr-flex cr-justify-between cr-align-center">
              <Bone width="10%" height="1rem" />
              <Bone width="10%" height="1rem" />
              <Bone width="10%" height="1rem" />
            </div>
          </CardSection>
          <CardSection>
            <div className="cr-flex cr-justify-between cr-align-center">
              <Bone width="20%" height="1rem" />
              <Bone width="15%" height="1rem" />
              <Bone width="15%" height="1rem" />
            </div>
          </CardSection>
        </Card>
      </LayoutContent>
    ) : (
      gatewayConfig.map(gateway => {
        const { hasTerminalSupport, iOSGatewayOnly, hasSwiperSupportOnly } =
          find(gatewayDetails, details => details.gatewayID === gateway.gatewayID) || {};

        const terminals = filter(
          configuredTerminals,
          terminal => terminal.gatewayConfigID === gateway.gatewayConfigID
        );

        return (
          <LayoutContent key={gateway.gatewayConfigID}>
            <GatewayTerminalListing
              gatewayName={gateway.name}
              hasTerminalSupport={hasTerminalSupport}
              iOSGatewayOnly={iOSGatewayOnly}
              hasSwiperSupportOnly={hasSwiperSupportOnly}
              terminals={terminals}
              onEdit={() => {
                history.push(paths.editGateway.replace(':gatewayId', gateway.gatewayConfigID));
              }}
              onEditTerminal={terminal => {
                this.setState({
                  openEditTerminal: true,
                  gatewayConfig: gateway,
                  ccTerminal: terminal,
                });
              }}
              onAddTerminal={() => {
                this.setState({
                  openAddTerminal: true,
                  gatewayConfig: gateway,
                });
              }}
              onDeleteTerminal={terminalID => {
                deleteTerminal(terminalID);
              }}
            />
          </LayoutContent>
        );
      })
    );

    return listings;
  }

  render() {
    const {
      lsGatewayConfig,
      isLoading,
      isErroring,
      isSavingTerminal,
      terminalParameters,
      ccTerminalErrors,
    } = this.props;
    const { openAddTerminal, openEditTerminal, gatewayConfig = {}, ccTerminal = {} } = this.state;

    return (
      <ViewError isErroring={isErroring}>
        <Layout>
          <LayoutContent>
            <LightspeedPayments
              isLoading={isLoading}
              gatewayConfig={lsGatewayConfig}
              onConfigureClick={this.handleConfigureClicked}
            />
          </LayoutContent>
          {this.renderGatewayTerminalListings()}
          {!isLoading && (
            <LayoutContent>
              <div className="cr-flex cr-items-center cr-justify-center">
                <Button variant="secondary" fill onClick={this.handleAddGatewayClicked}>
                  {STRINGS.BUTTON.ADD_PAYMENT_GATEWAY}
                </Button>
              </div>
            </LayoutContent>
          )}
        </Layout>
        <AddTerminal
          isOpen={openAddTerminal}
          onCancel={() => {
            this.setState({ openAddTerminal: false });
          }}
          onSuccess={this.createTerminal}
          isProcessing={isSavingTerminal}
          terminalParameters={terminalParameters}
          terminalGatewayID={gatewayConfig.gatewayID}
          ccTerminalErrors={ccTerminalErrors ? concat(ccTerminalErrors) : []}
        />
        <EditTerminal
          isOpen={openEditTerminal}
          onCancel={() => {
            this.setState({ openEditTerminal: false });
          }}
          onSuccess={this.editTerminal}
          isProcessing={isSavingTerminal}
          terminalParameters={terminalParameters}
          gatewayConfig={gatewayConfig}
          ccTerminal={ccTerminal}
          ccTerminalErrors={ccTerminalErrors ? concat(ccTerminalErrors) : []}
        />
      </ViewError>
    );
  }
}

PaymentProcessing.defaultProps = {
  isLoading: true,
  isErroring: false,
};

const mapStateToProps = state => ({
  lsGatewayConfig: getLightspeedPaymentGateways(state),
  gatewayConfig: getIntegratedPaymentGateways(state),
  paymentTypes: getMappablePaymentTypes(state),
  cardMappings: getLightspeedPaymentCardMappings(state),
  gatewayDetails: getGatewayDetails(state),
  configuredTerminals: getValues(entities.ccTerminal)(state),
  terminalParameters: getValues(entities.terminal)(state),
  isErroring: !!(
    getErrors({ entity: entities.gatewayConfig, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getErrors({ entity: entities.gateway, list: true, actionType: actionTypes.LOADING })(state) ||
    getErrors({ entity: entities.ccTerminal, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getErrors({ entity: entities.terminal, list: true, actionType: actionTypes.LOADING })(state)
  ),
  isLoading: !!(
    getProcessing({ entity: entities.gatewayConfig, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.gateway, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.ccTerminal, list: true, actionType: actionTypes.LOADING })(
      state
    ) ||
    getProcessing({ entity: entities.terminal, list: true, actionType: actionTypes.LOADING })(state)
  ),
  isSavingTerminal: getProcessing({
    entity: entities.ccTerminal,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
  ccTerminalErrors: getErrors({
    entity: entities.ccTerminal,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  dispatch,
  getData: (url, entity) => dispatch(getData({ url, entity, list: true })),
  clearData: () => {
    dispatch(
      clearData([entities.gatewayConfig, entities.shop, entities.ccTerminal, entities.paymentType])
    );
  },
  addTerminal: (terminalData, onSuccess) => {
    dispatch(
      postData({
        url: `/API/Account/${merchantos.account.id}/CCTerminal.json`,
        payload: terminalData,
        entity: entities.ccTerminal,
        onSuccess,
      })
    );
  },
  updateTerminal: (terminalID, terminalData, onSuccess) => {
    dispatch(
      putData({
        url: `/API/Account/${merchantos.account.id}/CCTerminal/${terminalID}.json`,
        payload: terminalData,
        entity: entities.ccTerminal,
        onSuccess,
      })
    );
  },
  deleteTerminal: terminalID => {
    dispatch(
      deleteData({
        url: `/API/Account/${merchantos.account.id}/CCTerminal/${terminalID}.json`,
        id: terminalID,
        entity: entities.ccTerminal,
      })
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentProcessing);
